import { AfterViewInit, Component, effect, ElementRef, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID, QueryList, Signal, ViewChildren, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CreateProductScanDataRequest, Product, ProductDesignTemplatePageContent } from '../../../store/app.model';
import { AppStateSelectors } from '../../../store/app.selectors';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngxs/store';
import { CreateProductScanData, GetProduct, SetTenantId, SetQRCode } from '../../../store/app.actions';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { GoogleMapsModule } from '@angular/google-maps';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { TextareaModule } from 'primeng/textarea';
import { DividerModule } from 'primeng/divider';
import { FooterComponent } from '../../components/footer/footer.component';
import { HeaderComponent } from '../../components/header/header.component';
import { TextImageVideoComponent } from '../../components/text-image-video/text-image-video.component';
import { TrackTraceComponent } from '../../components/track-trace/track-trace.component';
import { RatingComponent } from '../../components/rating/rating.component';
import { FormComponent } from '../../components/form/form.component';
import { EmailComponent } from '../../components/email/email.component';
import { SurveyComponent } from '../../components/survey/survey.component';
import { SocialLinksComponent } from '../../components/social-links/social-links.component';
import { ProductsComponent } from '../../components/products/products.component';
import { CarouselComponent } from '../../components/carousel/carousel.component';
import { TextComponent } from '../../components/text/text.component';
import { AttributesComponent } from '../../components/attributes/attributes.component';
import { VideoComponent } from '../../components/video/video.component';
import { BrandCardComponent } from '../../components/brand-card/brand-card.component';
import { DigitalExpirationCounterComponent } from '../../components/digital-expiration-counter/digital-expiration-counter.component';
import { ChartConfiguration } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { QrCode } from '../../components/qr-code/qr-code';
import { Authenticity } from '../../components/authenticity/authenticity';
import { DownloadableFiles } from '../../components/downloadable-files/downloadable-files';
import { filter, Subject, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LanguageSelectorComponent } from '../../components/language-selector/language-selector.component';
import { TranslationService } from '../../../services/translation.service';
import { withUrduFontFallback } from '../../../services/global.service';
import { PrivacyService } from '../../services/privacy.service';
import { RecallViewComponent } from '../../components/recall-view/recall-view.component';
import { resolveScanTenantId } from '../../utils/tenant-mapping';
import { LoaderComponent } from '../../components/loader/loader.component';

declare let gtag: (...args: any[]) => void;

@Component({
  selector: 'app-product-scan',
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    RecallViewComponent,
    DynamicDialogModule,
    GoogleMapsModule,
    ButtonModule,
    FormsModule,
    DividerModule,
    TextareaModule,
    FooterComponent,
    HeaderComponent,
    TextImageVideoComponent,
    TrackTraceComponent,
    RatingComponent,
    FormComponent,
    EmailComponent,
    SurveyComponent,
    SocialLinksComponent,
    ProductsComponent,
    CarouselComponent,
    TextComponent,
    AttributesComponent,
    VideoComponent,
    BrandCardComponent,
    DigitalExpirationCounterComponent,
    BaseChartDirective,
    QrCode,
    Authenticity,
    DownloadableFiles,
    LanguageSelectorComponent,
    LoaderComponent
  ],
  providers: [
    DialogService
  ],
  standalone: true,
  templateUrl: './product-scan.component.html',
  styles: [`
    .location-spinner {
      animation: location-spin 0.8s linear infinite;
    }
    @keyframes location-spin {
      to { transform: rotate(360deg); }
    }
  `],
})
export class ProductScanComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('gaSection', { read: ElementRef })
  private readonly gaSections!: QueryList<ElementRef>;
  private destroy$ = new Subject<void>();
  private intersectionObserver?: IntersectionObserver;

  bucketUrl = environment.bucketUrl;
  pageContent: ProductDesignTemplatePageContent[] = [];
  latitude = 0;
  longitude = 0;
  device = 'web';
  product$: Signal<Product | null>;
  isProcessing$: Signal<boolean>;
  isTagNotFound$: Signal<boolean>;
  isLoaderVisible = signal(false);
  private loaderTimeout: any;
  private readonly MIN_LOADER_DURATION = 3000;
  private loaderStartTime: number = 0;

  private scrollThresholds = [25, 50, 75, 100];
  private sentThresholds = new Set<number>();

  qrCode = '';
  tenantId = 'regenesis';

  showLocationPopover = false;
  locationError = '';
  locationStep: 'ask' | 'waiting' | 'blocked' | 'unavailable' = 'ask';
  browserName = 'your browser';
  private textNodeOriginalTextMap = new WeakMap<Text, string>();
  private placeholderOriginalTextMap = new WeakMap<HTMLElement, string>();
  private hasAppliedScanTranslationPreference = false;

  // Flag to prevent duplicate location calls
  private locationRecorded = false;
  private lastLocationProductId: string | number | null = null;

  // Chart.js (ng2-charts) support for weight, temperature, and humidity graphs
  public weightChartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };
  public tempChartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };
  public humChartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };

  // Colorblind-friendly palette
  private weightColor = '#0072B2'; // blue
  private tempColor = '#E69F00';   // orange
  private humColor = '#009E73';    // green

  public isMobile(): boolean {
    return window.innerWidth <= 600;
  }

  public weightChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} kg\n${ctx.label}`
        }
      }
    },
    scales: {
      x: {
        type: 'category', // Ensure date/time labels are used
        title: { display: true },
        ticks: {
          maxTicksLimit: this.isMobile() ? 4 : 8,
          font: { size: this.isMobile() ? 10 : 12 }
        }
      },
      y: {
        title: { display: true, text: 'Weight' },
        ticks: { font: { size: this.isMobile() ? 10 : 12 } }
      }
    }
  };

  public tempChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} °C\n${ctx.label}`
        }
      }
    },
    scales: {
      x: {
        type: 'category', // Ensure date/time labels are used
        title: { display: true },
        ticks: {
          maxTicksLimit: this.isMobile() ? 4 : 8,
          font: { size: this.isMobile() ? 10 : 12 },
        }
      },
      y: {
        title: { display: true, text: '°C' },
        ticks: { font: { size: this.isMobile() ? 10 : 12 } }
      }
    }
  };

  public humChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} %\n${ctx.label}`
        }
      }
    },
    scales: {
      x: {
        type: 'category', // Ensure date/time labels are used
        title: { display: true },
        ticks: {
          maxTicksLimit: this.isMobile() ? 4 : 8,
          font: { size: this.isMobile() ? 10 : 12 },
        }
      },
      y: {
        title: { display: true, text: '%' },
        ticks: { font: { size: this.isMobile() ? 10 : 12 } }
      }
    }
  };

  private toChartData(history: { value: number, createdAt: string }[], label: string, color: string): ChartConfiguration<'line'>['data'] {
    const sorted = [...history].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return {
      labels: sorted.map(item => {
        const d = new Date(item.createdAt);
        // Convert to local timezone and format as 'MMM dd, hh:mm AM/PM'
        return d.toLocaleString(undefined, {
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }).replace(',', ''); // Remove comma between day and time
      }),
      datasets: [
        {
          data: sorted.map(item => item.value),
          label,
          fill: false,
          borderColor: color,
          borderWidth: 2, // Make the line visible
          backgroundColor: color + '33', // Optional: semi-transparent fill for points
          tension: 0.4,
          pointBackgroundColor: color,
          pointRadius: 0, // Hide points
          pointHoverRadius: 0, // Hide points on hover
          showLine: true // Ensure the line is shown
        }
      ]
    };
  }

  private store = inject(Store);
  private sanitizer = inject(DomSanitizer);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private translationService = inject(TranslationService);
  private readonly privacyService = inject(PrivacyService);

  constructor() {
    effect(() => {
      const isProcessing = this.isProcessing$();
      const product = this.product$();
      const isTagNotFound = this.isTagNotFound$();

      // Loader should be visible if we are still processing OR if we don't have product data yet (and it's not a "tag not found" scenario)
      const isStillWorking = isProcessing || (product === null && !isTagNotFound);

      if (isStillWorking) {
        if (!this.isLoaderVisible()) {
          this.loaderStartTime = Date.now();
          this.isLoaderVisible.set(true);
        }
        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
          this.loaderTimeout = null;
        }
      } else if (this.isLoaderVisible()) {
        const elapsedTime = Date.now() - this.loaderStartTime;
        // Ensure at least MIN_LOADER_DURATION total, and provide a 200ms rendering buffer after data is ready
        const remainingTime = Math.max(200, this.MIN_LOADER_DURATION - elapsedTime);

        if (this.loaderTimeout) clearTimeout(this.loaderTimeout);

        this.loaderTimeout = setTimeout(() => {
          this.isLoaderVisible.set(false);
        }, remainingTime);
      }
    });

    // Robust QR code extraction for both URL patterns
    let qrCode =
      this.activatedRoute.snapshot.queryParams['qrcode'] ||
      this.activatedRoute.snapshot.params['qrCode'];
    if (!qrCode) {
      // Fallback: parse last segment of the path
      const urlSegments = this.activatedRoute.snapshot.url;
      if (urlSegments && urlSegments.length > 0) {
        qrCode = urlSegments.at(-1)?.path;
      } else {
        // As a last resort, parse from the full URL
        const path = window.location.pathname;
        const parts = path.split('/').filter(Boolean);
        if (parts.length > 0) {
          qrCode = parts[parts.length - 1];
        }
      }
    }

    // GS1 routes need GTIN to disambiguate per-product serials:
    // - /product/:gtin/details/:qrCode
    // - /01/:gtin/... digital links (qrcode often in ?qrcode=)
    let routeGtin =
      this.activatedRoute.snapshot.params['gtin'] ||
      this.activatedRoute.snapshot.queryParams['gtin'] ||
      null;
    if (!routeGtin) {
      const pathParts = window.location.pathname
        .split('/')
        .filter(Boolean);
      const ai01 = pathParts.indexOf('01');
      if (ai01 >= 0 && pathParts[ai01 + 1]) {
        routeGtin = pathParts[ai01 + 1];
      }
    }

    // Compact codes like 6-1-1o encode the tenant. That wins over a default
    // or leftover x-tenant-id=regenesis from qa-scan.regenesis.ag / the camera.
    const queryTenantId = this.activatedRoute.snapshot.queryParams['x-tenant-id'];
    this.tenantId = resolveScanTenantId(qrCode, queryTenantId);
    this.qrCode = qrCode;

    if (queryTenantId !== this.tenantId) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { 'x-tenant-id': this.tenantId },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    // Set tenant ID in the state
    this.store.dispatch(new SetTenantId(this.tenantId));

    // Note: tag-not-found state and authentication state are managed in the store
    // No need to check query parameters - the store state is the source of truth

    // Ensure QR code is set in store if not already set
    if (!this.store.selectSnapshot(AppStateSelectors.getQRCode)) {
      this.store.dispatch(new SetQRCode(this.qrCode));
    }

    // Debug logs for qrCode and tenantId
    this.product$ = this.store.selectSignal(AppStateSelectors.getProduct);
    this.isProcessing$ = this.store.selectSignal(AppStateSelectors.isProcessing);
    this.isTagNotFound$ = this.store.selectSignal(AppStateSelectors.isTagNotFound);

    // Debug: Log current store state
    console.log('🔍 Product Scan Component - Initial State:', {
      qrCode: this.qrCode,
      tenantId: this.tenantId,
      storeQRCode: this.store.selectSnapshot(AppStateSelectors.getQRCode),
      storeTenantId: this.store.selectSnapshot(AppStateSelectors.getTenantId),
      storeIsTagNotFound: this.store.selectSnapshot(AppStateSelectors.isTagNotFound),
      storeIsAuthenticated: this.store.selectSnapshot(AppStateSelectors.isAuthenticated)
    });

    this.store.dispatch(
      new GetProduct(this.qrCode, this.tenantId, routeGtin)
    );

    effect(() => {
      const product = this.product$();
      // Debug log for product
      if (product) {
        console.log('🔍 Product loaded, setting up component:', {
          productId: product.id,
          productName: product.productName,
          isTagNotFound: this.isTagNotFound$(),
          isAuthenticated: this.store.selectSnapshot(AppStateSelectors.isAuthenticated)
        });

        if (this.lastLocationProductId !== product.id) {
          this.locationRecorded = false;
          this.hasAppliedScanTranslationPreference = false;
          this.lastLocationProductId = product.id;
        }

        this.pageContent = [];
        if (product.productDesignTemplate && product.productDesignTemplate.pageContent) {
          product.productDesignTemplate.pageContent
            .sort((a: ProductDesignTemplatePageContent, b: ProductDesignTemplatePageContent) => a.order - b.order)
            .forEach((content: ProductDesignTemplatePageContent) => {
              this.pageContent.push(content);
            });
        }

        this.weightChartData = this.toChartData((product as unknown as { weightHistory: any[] }).weightHistory || [], 'Weight', this.weightColor);
        this.tempChartData = this.toChartData((product as unknown as { temperatureHistory: any[] }).temperatureHistory || [], 'Temperature', this.tempColor);
        this.humChartData = this.toChartData((product as unknown as { humidityHistory: any[] }).humidityHistory || [], 'Humidity', this.humColor);

        this.prepareLocation();
        this.applyScanTranslationPreference();

        // Track QR code view in Google Analytics
        if (typeof (globalThis as any)['gtag'] === 'function') {
          (globalThis as any)['gtag']('set', 'user_properties', {
            public_experience_id: product!.productDesignTemplate!.id,
            qr_code: product.qrProductItem.qrcode,
            tenant_id: this.tenantId,
            product_id: product.id,
            product_name: product.productName || 'Unknown',
            organization_id: product.organizationId.toString()
          });
          // THIS IS REQUIRED - Send the actual event to record the QR code view
          (window as any).gtag('event', 'qr_code_view', {
            public_experience_id: product!.productDesignTemplate!.id,
            qr_code: `QR-${product.qrProductItem.qrcode}`,
            tenant_id: this.tenantId,
            product_id: product.id,
            product_name: product.productName || 'Unknown',
            organization_id: product.organizationId.toString()
          });
        }

        const currentLang = this.translationService.getCurrentLanguage();
        if (currentLang !== 'en') {
          console.log(`🌐 [ProductScan] Product data ready — translation will fire when loader hides`);
        }
      } else {
        this.pageContent = [];
        this.weightChartData = { labels: [], datasets: [] };
        this.tempChartData = { labels: [], datasets: [] };
        this.humChartData = { labels: [], datasets: [] };
      }
    });

    // ─── Translation trigger: fires when loader hides (product content is in DOM) ───
    effect(() => {
      const loaderVisible = this.isLoaderVisible();
      if (!loaderVisible && this.product$()) {
        const lang = this.translationService.getCurrentLanguage();
        if (lang !== 'en') {
          console.log(`🌐 [ProductScan] Loader hidden — translating to "${lang}"`);
          // setTimeout(0) lets Angular flush the DOM before we walk it
          setTimeout(() => this.translatePageContent(lang), 0);
        }
      }
    });

    this.detectDevice();

    this.gaSections?.changes
      .pipe(filter(list => list.length > 0), takeUntilDestroyed())
      .subscribe(() => this.setupSectionTracking());
  }

  ngOnInit(): void {
    window.addEventListener('languageChanged', ((event: CustomEvent) => {
      const langCode = event.detail.languageCode;
      console.log(`🌐 [ProductScan] languageChanged event received: "${langCode}"`);
      // Small delay to let Angular process any pending change detection
      setTimeout(() => this.translatePageContent(langCode), 100);
    }) as EventListener);

    const currentLang = this.translationService.getCurrentLanguage();
    console.log(`🌐 [ProductScan] ngOnInit — current language: "${currentLang}"`);
  }

  private applyScanTranslationPreference(): void {
    if (this.hasAppliedScanTranslationPreference) {
      return;
    }

    const product = this.store.selectSnapshot(AppStateSelectors.getProduct);
    const useLocationBasedTranslation = Boolean(
      product?.productDesignTemplate?.useLocationBasedTranslation
    );
    console.log(`🌐 [ProductScan] useLocationBasedTranslation=${useLocationBasedTranslation}`);

    this.hasAppliedScanTranslationPreference = true;

    if (!useLocationBasedTranslation) {
      this.translationService.applyDefaultEnglish();
      window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { languageCode: 'en' }
      }));
      return;
    }

    if (this.locationRecorded) {
      this.translationService.detectLanguageFromCoordinates(this.latitude, this.longitude);
    }
  }

  private getPreferredLocalLanguage(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return 'en';
    }

    const browserLanguage = (
      navigator.languages?.[0] ||
      navigator.language ||
      'en'
    ).toLowerCase();

    const supportedLanguageCodes = this.translationService.supportedLanguages.map(lang => lang.code.toLowerCase());
    if (supportedLanguageCodes.includes(browserLanguage)) {
      return browserLanguage;
    }

    const primaryLanguage = browserLanguage.split('-')[0];
    if (supportedLanguageCodes.includes(primaryLanguage)) {
      return primaryLanguage;
    }

    return 'en';
  }

  translatePageContent(targetLanguage: string, retried = false): void {
    if (targetLanguage === 'en') {
      this.resetToOriginalContent();
      return;
    }

    console.log(`🌐 [translatePageContent] Called with language: "${targetLanguage}"`);

    const textNodes: { node: Text; text: string }[] = [];
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;

          const tagName = parent.tagName.toLowerCase();
          if (['script', 'style', 'noscript', 'input', 'textarea'].includes(tagName)) {
            return NodeFilter.FILTER_REJECT;
          }

          if (
            parent.closest('app-language-selector') ||
            parent.closest('.language-selector-container') ||
            parent.closest('.p-select')
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          const text = node.textContent?.trim();
          if (text && text.length > 0 && text.length < 500) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        }
      }
    );

    let node: Node | null = null;
    while ((node = walker.nextNode()) !== null) {
      const text = node.textContent?.trim();
      if (text && text.length > 0) {
        textNodes.push({ node: node as Text, text });
      }
    }

    console.log(`🌐 [translatePageContent] Found ${textNodes.length} text nodes`);
    if (textNodes.length === 0) {
      if (!retried) {
        console.warn('🌐 [translatePageContent] No text nodes found — retrying in 2000ms');
        setTimeout(() => this.translatePageContent(targetLanguage, true), 2000);
      } else {
        console.warn('🌐 [translatePageContent] No text nodes found on retry — giving up');
      }
      return;
    }

    const uniqueTexts = Array.from(new Set(textNodes.map(t => t.text)));

    const inputElements: { element: HTMLInputElement | HTMLTextAreaElement; placeholder: string }[] = [];
    const allInputs = document.querySelectorAll('input[placeholder], textarea[placeholder]');
    allInputs.forEach((el) => {
      const element = el as HTMLInputElement | HTMLTextAreaElement;
      const placeholder = element.getAttribute('placeholder');
      if (placeholder && placeholder.trim().length > 0 && placeholder.trim().length < 500) {
        if (
          element.closest('app-language-selector') ||
          element.closest('.language-selector-container') ||
          element.closest('.p-select')
        ) {
          return;
        }
        inputElements.push({ element, placeholder: placeholder.trim() });
      }
    });

    const allTextsToTranslate = [...uniqueTexts, ...Array.from(new Set(inputElements.map(e => e.placeholder)))];

    if (allTextsToTranslate.length === 0) return;

    console.log(`🌐 [translatePageContent] Sending ${allTextsToTranslate.length} strings to API`);

    this.translationService.translateBatch(allTextsToTranslate, targetLanguage).subscribe({
      next: (translations) => {
        console.log(`🌐 [translatePageContent] Got ${translations.length} translations, applying...`);
        const translationMap = new Map<string, string>();
        allTextsToTranslate.forEach((text, index) => {
          if (translations[index] && translations[index] !== text) {
            translationMap.set(text, translations[index]);
          }
        });

        textNodes.forEach(({ node, text }) => {
          const translatedText = translationMap.get(text);
          if (translatedText && node.parentElement) {
            if (!this.textNodeOriginalTextMap.has(node)) {
              this.textNodeOriginalTextMap.set(node, text);
            }
            node.textContent = translatedText;
          }
        });

        inputElements.forEach(({ element, placeholder }) => {
          const translatedPlaceholder = translationMap.get(placeholder);
          if (translatedPlaceholder && translatedPlaceholder !== placeholder) {
            if (!this.placeholderOriginalTextMap.has(element)) {
              this.placeholderOriginalTextMap.set(element, placeholder);
            }
            element.setAttribute('placeholder', translatedPlaceholder);
          }
        });
      },
      error: (err) => {
        console.error('🌐 [translatePageContent] Translation API error:', err);
      }
    });
  }

  resetToOriginalContent(): void {
    setTimeout(() => {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;

            const tagName = parent.tagName.toLowerCase();
            if (['script', 'style', 'noscript', 'input', 'textarea'].includes(tagName)) {
              return NodeFilter.FILTER_REJECT;
            }

            if (parent.closest('app-language-selector') || parent.closest('.language-selector-container') || parent.closest('.p-select')) {
              return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      let textNode: Node | null = null;

      while ((textNode = walker.nextNode()) !== null) {
        const originalText = this.textNodeOriginalTextMap.get(textNode as Text);
        if (originalText && textNode.textContent !== originalText) {
          textNode.textContent = originalText;
          this.textNodeOriginalTextMap.delete(textNode as Text);
        }
      }

      const allInputs = document.querySelectorAll('input[placeholder], textarea[placeholder]');
      allInputs.forEach((el) => {
        const element = el as HTMLInputElement | HTMLTextAreaElement;
        if (element.closest('app-language-selector') || element.closest('.language-selector-container') || element.closest('.p-select')) {
          return;
        }
        const originalPlaceholder = this.placeholderOriginalTextMap.get(element);
        if (originalPlaceholder) {
          element.setAttribute('placeholder', originalPlaceholder);
          this.placeholderOriginalTextMap.delete(element);
        }
      });
    }, 100);
  }

  ngAfterViewInit() {
    this.gaSections.changes
      .pipe(
        filter(list => list.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.setupSectionTracking());

    this.setupSectionTracking();
  }

  private setupSectionTracking(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const seen = new Set<string>();
    this.intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('id');
          const product = this.product$();
          console.log('🔍 sectionId', sectionId);
          if (sectionId && !seen.has(sectionId) && typeof (window as any).gtag === 'function' && product) {
            seen.add(sectionId);
            (window as any).gtag('event', 'section_view', {
              public_experience_id: product!.productDesignTemplate!.id,
              tenant_id: this.tenantId,
              product_id: product.id,
              product_name: product.productName || 'Unknown',
              organization_id: product.organizationId.toString(),
              section_name: sectionId,
              page_path: window.location.pathname
            });
          }
        }
      });
    }, { threshold: 0.5 });

    this.gaSections.forEach(sectionRef => {
      this.intersectionObserver!.observe(sectionRef.nativeElement);
    });
  }

  @HostListener('window:scroll', [])
  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = (scrollTop / docHeight) * 100;
    const product = this.product$();
    for (const threshold of this.scrollThresholds) {
      if (scrollPercent >= threshold && !this.sentThresholds.has(threshold) && product) {
        this.sentThresholds.add(threshold);

        // Send event to GA4
        gtag('event', 'scroll_depth', {
          percent_scrolled: threshold,
          public_experience_id: product!.productDesignTemplate!.id,
          tenant_id: this.tenantId,
          product_id: product.id,
          product_name: product.productName || 'Unknown',
          organization_id: product.organizationId.toString(),
          page_path: window.location.pathname
        });

        console.log(`Scrolled ${threshold}% of page`);
      }
    }
  }

  ngOnDestroy() {
    this.sentThresholds.clear();

    this.destroy$.next();
    this.destroy$.complete();
    this.sentThresholds.clear();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  redirect() {
    if (!this.product$()?.productDesignTemplate?.publicDisplayUrl) {
      return;
    }
    window.location.href = (this.product$()?.productDesignTemplate?.publicDisplayUrl as string);
  }

  detectDevice() {
    const ua = navigator.userAgent;

    if (/CriOS/i.test(ua) || (/Chrome/i.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua))) {
      this.browserName = 'Chrome';
    } else if (/SamsungBrowser/i.test(ua)) {
      this.browserName = 'Samsung Internet';
    } else if (/Edg/i.test(ua)) {
      this.browserName = 'Edge';
    } else if (/Safari/i.test(ua)) {
      this.browserName = 'Safari';
    } else {
      this.browserName = 'your browser';
    }

    if (/android/i.test(ua)) {
      this.device = 'android';
      return;
    }

    if (/iPhone|iPad|iPod/i.test(ua)) {
      this.device = 'ios';
      return;
    }

    // iPadOS (Safari reports as Mac but with touch)
    if (
      /Macintosh/i.test(ua) &&
      navigator.maxTouchPoints > 1
    ) {
      this.device = 'ios';
      return;
    }

    this.device = 'web';
  }

  private async queryLocationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        return result.state;
      }
    } catch {
      // Safari and some in-app browsers do not support this API
    }
    return 'unknown';
  }

  prepareLocation(): void {
    if (this.locationRecorded) {
      return;
    }

    if (!navigator.geolocation) {
      this.continueWithoutLocation();
      return;
    }

    void this.queryLocationPermission().then((permission) => {
      if (this.locationRecorded) {
        return;
      }

      if (permission === 'granted') {
        this.captureLocation(false);
        return;
      }

      this.locationStep = permission === 'denied' ? 'blocked' : 'ask';
      this.showLocationPopover = true;
    });
  }

  enableLocation(): void {
    this.captureLocation(true);
  }

  tryAgain(): void {
    this.captureLocation(true);
  }

  private captureLocation(fromUserAction: boolean): void {
    if (this.locationRecorded) {
      this.showLocationPopover = false;
      return;
    }

    const product = this.product$();
    if (!product || !navigator.geolocation) {
      this.continueWithoutLocation();
      return;
    }

    if (fromUserAction) {
      this.locationStep = 'waiting';
      this.showLocationPopover = true;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.privacyService.setConsent({ location: true });
        this.recordScan(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.error('Error getting location:', error);
        this.showLocationPopover = true;

        if (error.code === error.PERMISSION_DENIED) {
          this.locationStep = 'blocked';
          this.locationError = 'Location is off.';
          return;
        }

        this.locationStep = 'unavailable';
        this.locationError = error.code === error.TIMEOUT
          ? 'This is taking too long.'
          : 'Your phone could not find you.';
      },
      {
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 60000
      }
    );
  }

  private recordScan(latitude: number, longitude: number): void {
    const product = this.product$();
    if (!product || this.locationRecorded) {
      this.showLocationPopover = false;
      return;
    }

    this.latitude = latitude;
    this.longitude = longitude;
    this.showLocationPopover = false;
    this.locationRecorded = true;

    if (product.productDesignTemplate?.useLocationBasedTranslation) {
      this.translationService.detectLanguageFromCoordinates(latitude, longitude);
    }

    const publicScanResults = this.store.selectSnapshot(AppStateSelectors.getPublicScanResults);
    const payload: CreateProductScanDataRequest = {
      latitude,
      longitude,
      qrProductItemId: product.qrProductItem.id,
      organizationId: product.organizationId,
      productId: product.id,
      device_type: this.device,
      os_version: 'unknown',
      scanValidationStatus: this.isTagNotFound$() ? 'SUSPICIOUS' : 'VALID',
      scanValidationMessage: this.isTagNotFound$() ? 'Product not found in system' : 'Product found and validated successfully',
      publicScanResults: publicScanResults || null
    };

    this.store.dispatch(
      new CreateProductScanData(payload, this.tenantId)
    ).subscribe(() => {
      this.redirect();
    });
  }

  continueWithoutLocation(): void {
    if (this.locationRecorded) {
      this.showLocationPopover = false;
      return;
    }

    const product = this.product$();
    if (!product) {
      return;
    }

    this.showLocationPopover = false;
    this.locationRecorded = true;

    const publicScanResults = this.store.selectSnapshot(AppStateSelectors.getPublicScanResults);
    const payload: CreateProductScanDataRequest = {
      latitude: 0,
      longitude: 0,
      qrProductItemId: product.qrProductItem.id,
      organizationId: product.organizationId,
      productId: product.id,
      device_type: this.device,
      os_version: 'unknown',
      scanValidationStatus: this.isTagNotFound$() ? 'SUSPICIOUS' : 'VALID',
      scanValidationMessage: this.isTagNotFound$() ? 'Product not found in system' : 'Product found and validated successfully',
      publicScanResults: publicScanResults || null
    };

    this.store.dispatch(
      new CreateProductScanData(payload, this.tenantId)
    ).subscribe(() => {
      this.redirect();
    });
  }

  refreshPage(): void {
    window.location.reload();
  }

  getSafeEmbedUrl(videoUrl: string | undefined | null): SafeResourceUrl | null {
    try {
      if (!videoUrl) return null;

      const url = new URL(videoUrl);
      const hostname = url.hostname.replace('www.', '');
      const youtubeDomains = ['youtube.com', 'youtu.be'];
      const vimeoDomains = ['vimeo.com'];

      let embedUrl: string | null = null;

      if (youtubeDomains.includes(hostname)) {
        let videoId: string | null = null;

        if (url.pathname.startsWith('/shorts/')) {
          // YouTube Shorts
          videoId = url.pathname.split('/')[2];
        } else if (hostname === 'youtu.be') {
          // youtu.be short link
          videoId = url.pathname.substring(1);
        } else if (url.pathname.startsWith('/embed/')) {
          // Already an embed link – return as is
          return this.sanitizer.bypassSecurityTrustResourceUrl(videoUrl);
        } else if (url.pathname === '/watch') {
          // Standard YouTube link
          videoId = url.searchParams.get('v');
        }

        if (!videoId) throw new Error('YouTube Video ID not found');
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (vimeoDomains.includes(hostname)) {
        // Vimeo
        const vimeoIdMatch = url.pathname.match(/\/(\d+)/);
        const videoId = vimeoIdMatch ? vimeoIdMatch[1] : null;
        if (!videoId) throw new Error('Vimeo Video ID not found');
        embedUrl = `https://player.vimeo.com/video/${videoId}`;
      } else {
        throw new Error('Unsupported video URL');
      }

      return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    } catch (error: any) {
      console.error('Error converting to safe embed URL:', error.message);
      return null;
    }
  }

  getSafeIframeUrl(url: string | undefined | null): SafeResourceUrl | null {
    if (!url) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  filter(array: any[] | undefined | null, column: any, value: any) {
    if (!array) return [];
    return array.filter(item => item[column] === value);
  }

  buildStyle(type: string, style: {
    color?: string;
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    textAlign?: string;
    backgroundColor?: string;
  }): string {
    if (!style) return '';
    if (type === 'text' || type === 'title' || type === 'description' || type === 'date') {
      return `
        color: ${style.color};
        font-size: ${style.fontSize};
        font-weight: ${style.fontWeight};
        font-family: ${withUrduFontFallback(style.fontFamily)} !important;
        text-align: ${style.textAlign};
      `;
    }
    if (type === 'button') {
      return `
        color: ${style.color};
        background-color: ${style.backgroundColor};
        font-size: ${style.fontSize};
        font-weight: ${style.fontWeight};
        font-family: ${withUrduFontFallback(style.fontFamily)} !important;
      `;
    }
    return ``;
  }

  downloadChart(type: 'weight' | 'temp' | 'hum') {
    // Find the canvas element for the chart
    let canvas: HTMLCanvasElement | null = null;
    if (type === 'weight') {
      canvas = document.querySelector('canvas[aria-label="Weight history line chart"]');
    } else if (type === 'temp') {
      canvas = document.querySelector('canvas[aria-label="Temperature history line chart"]');
    } else if (type === 'hum') {
      canvas = document.querySelector('canvas[aria-label="Humidity history line chart"]');
    }
    if (canvas) {
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${type}-history-chart.png`;
      link.click();
    }
  }
}
